import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { firePAEvent } from './utils/analytics';

/*
 * Homepage (31 Aug 2026 rebuild) — the employer landing.
 *
 * One customer: the employer. The previous homepage addressed individuals
 * and physios and never the person who buys, which is the defect the board
 * named. Individuals and partners are now supporting surfaces reachable
 * from the footer.
 *
 * Four sections, each roughly a viewport:
 *   1  Coverage      equity of access, the door 3 wedge
 *   2  Visibility    CMPX, stated as a reveal rather than a feature
 *   3  Personal      SteadyStride, the load contrast
 *   4  The promise   what we commit to and what we do not claim
 *
 * The exit grid is fixed: Learn (article), Verify (reference), See
 * (product or tool), same order every section, third slot leading.
 * Full rationale in PO - Website/Mockups/Website_Wireframe_System.html.
 *
 * Claims: nothing here asserts a saving. "Prevention" is stated as timing,
 * never outcome. No biomarker language before Class IIa.
 */

const IconDoc = () => (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <rect x="9" y="12" width="30" height="24" rx="4" stroke="#9fe3ec" strokeWidth="2.4" />
        <path d="M16 22h10M16 28h16" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
);
const IconShield = () => (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <path d="M24 6l14 6v11c0 9-6 15-14 19-8-4-14-10-14-19V12l14-6z" stroke="#104378" strokeWidth="2.4" strokeLinejoin="round" />
        <path d="M18 24l4 4 9-9" stroke="#4CB6C3" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const IconMonitor = () => (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <rect x="6" y="10" width="36" height="24" rx="3" stroke="#104378" strokeWidth="2.4" />
        <path d="M18 40h12M24 34v6" stroke="#4CB6C3" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
);
const IconWave = () => (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <path d="M6 30c4 0 5-13 9-13s5 18 9 18 5-16 9-16 5 8 9 8" stroke="#4CB6C3" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const IconPerson = () => (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <circle cx="19" cy="17" r="7" stroke="#104378" strokeWidth="2.4" />
        <path d="M8 39c0-6 5-10 11-10s11 4 11 10" stroke="#104378" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M32 20l3 3 6-6" stroke="#4CB6C3" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const IconPhone = () => (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <rect x="15" y="6" width="18" height="36" rx="4" stroke="#104378" strokeWidth="2.4" />
        <path d="M21 12h6" stroke="#4CB6C3" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="24" cy="34" r="2.4" fill="#4CB6C3" />
    </svg>
);

const IconPayroll = () => (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect x="5" y="4" width="22" height="24" rx="3" stroke="#9fe3ec" strokeWidth="2" />
        <path d="M10 11h12M10 16h12" stroke="#9fe3ec" strokeWidth="2" strokeLinecap="round" />
        <path d="M10 21h7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeDasharray="2 3" />
    </svg>
);
const IconDial = () => (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="11" stroke="#9fe3ec" strokeWidth="2" />
        <path d="M16 16 L23 11" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeDasharray="2 3" />
        <circle cx="16" cy="16" r="2" fill="#9fe3ec" />
    </svg>
);
const IconWait = () => (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="11" stroke="#9fe3ec" strokeWidth="2" />
        <path d="M16 9v7l5 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const Exit = ({ to, icon, label, type, lead }) => (
    <Link className={lead ? 'ex lead' : 'ex'} to={to}>
        <span className="ic">{icon}</span>
        <span className="lb">{label}</span>
        <span className="ty">{type}</span>
    </Link>
);

const TITLE = 'PhysioActivity: Workplace musculoskeletal health for your whole workforce';
const DESCRIPTION = 'Workplace musculoskeletal health for everyone you cover, not only the people who ask for it. Each person gets a physiotherapy programme built from their own movement data, and you see how much of it is happening across your workforce.';

const HomePage = () => {
    // Every other route sets its own title in a useEffect. The homepage lost its
    // one in the rebuild, so arriving here from anywhere else left that page's
    // title in the tab. These strings match index.html deliberately: this page is
    // the brand-search result, so the two must not drift.
    useEffect(() => {
        document.title = TITLE;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', DESCRIPTION);
    }, []);

    return (
    <div className="pa2">

        {/* 1 · THE PROBLEM
            Rebuilt 1 Sep on the confirmed buyer why-now (S&M Master, addition to
            prompt 2). The three points run in the Master's order because the order
            is the argument. The confirmed set has four; row 3 — benefits reaching
            the already-active — was cut because it is the only one already
            published, as why-gym-memberships-fail. Deviation logged as DEF-02.

            The headline is the measurement gap rather than the confirmed line 1
            wording ("you are already paying for this"). That line addresses only
            the buyer and reads financial; this page is read by employees too. Line
            1 keeps its position as the first card. Logged as DEC-01, needs CMO.

            No presenteeism multiple appears here — those figures are with CMA
            (ESC-W-01). Row 3 says "waiting times", not "the NHS route": the site
            carries a disclaimer that we are not a replacement for NHS pathways.

            The section 1 exit grid was removed to bring the hero height down. That
            breaks the fixed Learn/Verify/See rule for this section — logged as
            DEF-01/REL-05, decision pending. */}
        <section className="hero" id="top">
            <div className="wrap two">
                <div className="heromain">
                    <h1 className="on-dark">
                        <span className="setup">MSK Health is the problem</span>
                        <span className="payoff">no one measures.</span>
                    </h1>
                    <p className="lede">
                        Musculoskeletal Health, MSK Health, is one of the biggest causes of lost working
                        days in the UK, and the one most workplaces only see once someone stops.
                    </p>
                    <p className="nerve">
                        So we measure it. Everyone you cover gets a programme personalised by a
                        physiotherapist, built from how they actually move.
                    </p>
                    <div className="cta-row">
                        <Link
                            className="btn btn-t"
                            to="/contact"
                            onClick={() => firePAEvent('employer_cta_click', { source: 'hero' })}
                        >
                            See what MSK Health costs you
                        </Link>
                    </div>
                </div>

                <div className="whyrow">
                    <div className="wc"><span className="ic"><IconPayroll /></span><span className="lb">It is already in your payroll, unlabelled.</span></div>
                    <div className="wc"><span className="ic"><IconDial /></span><span className="lb">Nothing shows you where the risk is.</span></div>
                    <div className="wc"><span className="ic"><IconWait /></span><span className="lb">Waiting times have closed the prevention route.</span></div>
                </div>
            </div>
        </section>

        {/* 2 · VISIBILITY */}
        <section className="tint" id="visibility">
            <div className="wrap">
                <div className="split">
                    <div className="col">
                        <h2>
                            <span className="setup">See it sooner.</span>
                            <span className="payoff">Support people earlier.</span>
                        </h2>
                        <p className="lede">
                            Your own CMPX login, showing take-up and adherence across everyone you
                            cover, not only the people who came forward. Workforce level, no group under
                            fifty shown, and no names.
                        </p>
                        <div className="exits">
                            <Exit to="/blog" icon={<IconDoc />} label="What you can see, and what you cannot" type="Article" />
                            <Exit to="/trust-centre" icon={<IconShield />} label="Trust centre" type="Reference" />
                            <Exit to="/employers" icon={<IconMonitor />} label="What you would see" type="For employers" lead />
                        </div>
                    </div>

                    <div className="col">
                        <div className="dash" aria-label="CMPX employer dashboard, illustrative">
                            <div className="d-top"><i /><i /><i /><span className="w">CMPX · employer view</span></div>
                            <div className="d-body">
                                <div className="d-side"><i className="on" /><i /><i /><i /><i /><i /></div>
                                <div className="d-main">
                                    <div className="d-h"><span className="t">Workforce MSK Health</span><span className="s">Whole-workforce level</span></div>
                                    <div className="d-tiles">
                                        <div><div className="v">128<small> / 150</small></div><div className="c">covered of workforce</div></div>
                                        <div><div className="v g">72%</div><div className="c">participating</div></div>
                                        <div><div className="v g">8 wks</div><div className="c">reporting window</div></div>
                                    </div>
                                    <div className="d-chart">
                                        <div className="cl">Adherence, last 8 weeks</div>
                                        <svg viewBox="0 0 320 74" width="100%" height="74" aria-hidden="true">
                                            <polyline points="0,62 45,54 90,57 135,42 180,36 225,27 270,22 320,15" fill="none" stroke="#4CB6C3" strokeWidth="3.4" strokeLinecap="round" />
                                            <polyline points="0,66 45,64 90,65 135,62 180,61 225,60 270,59 320,58" fill="none" stroke="#cbd6e0" strokeWidth="2.2" strokeDasharray="5 5" />
                                        </svg>
                                    </div>
                                    <div className="d-foot"><span>No group under 50 shown</span><span>Export · CSV, PDF</span></div>
                                </div>
                            </div>
                        </div>
                        <p className="cap">Illustrative example. Not a customer result.</p>
                    </div>
                </div>
            </div>
        </section>

        {/* 3 · PERSONAL */}
        <section className="white" id="personal">
            <div className="wrap">
                <div className="split rev">
                    <div className="col">
                        <div className="cluster">
                            <div className="watch">
                                <svg width="76" height="76" viewBox="0 0 60 60" aria-hidden="true">
                                    <circle cx="30" cy="26" r="18" fill="none" stroke="#123a5e" strokeWidth="5" />
                                    <circle cx="30" cy="26" r="18" fill="none" stroke="#4CB6C3" strokeWidth="5" strokeLinecap="round" strokeDasharray="113" strokeDashoffset="30" transform="rotate(-90 30 26)" />
                                    <text x="30" y="30" textAnchor="middle" fontSize="13" fontWeight="800" fill="#fff" fontFamily="Inter">81</text>
                                </svg>
                            </div>
                            <div className="phone">
                                <div className="ph-in">
                                    <div className="ph-hd">
                                        <div className="t">MIN LOADED</div>
                                        <div className="n">570</div>
                                        <div className="u">today</div>
                                    </div>
                                    <div className="ph-bar" />
                                    <div className="ph-lb">low · moderate · high</div>
                                    <div className="ph-list">
                                        <div className="ph-item done"><span className="dot" /><span><span className="a">Hip mobility</span><span className="b">Done · 6 min</span></span></div>
                                        <div className="ph-item"><span className="dot" /><span><span className="a">Loaded carries</span><span className="b">Today · 8 min</span></span></div>
                                        <div className="ph-item"><span className="dot" /><span><span className="a">Calf loading</span><span className="b">Today · 5 min</span></span></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p className="cap">Illustrative example. Not a customer result.</p>
                    </div>

                    <div className="col">
                        <h2>
                            <span className="setup">10,000 steps</span>
                            <span className="payoff">was never the target.</span>
                        </h2>
                        <p className="lede">
                            Nothing to log, nothing to remember. SteadyStride reads movement from the
                            phone they already own, and the programme changes as they do.
                        </p>
                        <div className="exits">
                            <Exit to="/blog" icon={<IconWave />} label="Not every step is equal" type="Article" />
                            <Exit to="/blog/who-writes-your-programme" icon={<IconPerson />} label="Who writes your programme" type="Reference" />
                            <Exit to="/individuals" icon={<IconPhone />} label="Get started" type="For individuals" lead />
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* 4 · THE CLOSE
            Was the promise section — Committed / Reported / Not claimed. Those
            four cards moved to /trust-centre on 2 Sep: they are contract
            language, answering a question only a buyer mid-evaluation asks,
            and they were the heaviest block on a page both audiences read. The
            hero's second button went with them. Logged as DEF-11. */}
        <section className="s-promise" id="close">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">Compare with</span>
                    <span className="payoff">the published benchmarks.</span>
                </h2>
                <p className="lede">
                    ONS and HSE figures, applied to your headcount, with every source shown and
                    the method open to check.
                </p>
                <div className="cta-row">
                    <Link
                        className="btn btn-t"
                        to="/contact"
                        onClick={() => firePAEvent('employer_cta_click', { source: 'closing' })}
                    >
                        See what MSK Health costs you
                    </Link>
                </div>
            </div>
        </section>

    </div>
    );
};

export default HomePage;
